/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useMemo } from 'react';
import styled from 'styled-components';
import { classNames } from 'primereact/utils';
import { Calendar, CalendarSync } from 'lucide-react';
import { DateTime } from 'luxon';
import { CMarkdown } from '@/components/CMarkdown.tsx';
import { Project, ProjectDefinition } from '@/types/models.ts';
import { useTranslation } from 'react-i18next';
import { GitBranch } from 'lucide-react';
import { QualityBadge } from '@/components/QualityBadge.tsx';
import { Panel } from 'primereact/panel';
import { GroupAvatar } from '@/components/GroupAvatar.tsx';
import { Link } from '@/components/Link.tsx';
import { linksDefinition, useProcessesContext } from 'ogc-processes-components';
import { useQuery } from '@tanstack/react-query';

export interface Props {
  project: Project;
  className?: string;
}

const Div = styled.div`
  .links {
    ul {
      li {
        margin-bottom: 8px;
      }
    }
  }
  img {
    max-width: 100%;
  }
`;

export const ProjectDescription: React.FC<Props> = ({ project, ...props }) => {
  const { t } = useTranslation();

  const { processesEndpoint } = useProcessesContext();

  const { data: projectDefinition } = useQuery({
    queryKey: ['get_links_project', project, processesEndpoint],
    queryFn: async () => {
      const rootLink = project.links.find((link) => link.rel === 'root')?.href;
      if (!rootLink) {
        return null;
      }
      return processesEndpoint.get<ProjectDefinition>(rootLink);
    },
    refetchOnWindowFocus: false
  });

  const links = useMemo(() => {
    const findLink = (rel: string) => projectDefinition?.links.find((link) => link.rel === rel)?.href;
    return {
      provider_link: project.url,
      api: findLink(linksDefinition.documentation) || '#',
      processes: findLink(linksDefinition.processes) || '#',
      conformance: findLink(linksDefinition.conformance) || '#'
    };
  }, [project, projectDefinition]);

  return (
    <Div
      className={classNames(
        props.className,
        'flex sm:flex-column md:flex-column lg:flex-row xl:flex-row justify-content-between gap-4'
      )}
    >
      <div className="sm:col-12 md:col-12  lg:col-7 xl:col-7 px-0">
        <div className="flex justify-content-end w-full">
          <QualityBadge size={'150px'} qualityScore={project.quality_score} />
        </div>
        <CMarkdown className="max-w-full overflow-auto" removeImages={false} markdown={project.readme ?? ''} />
      </div>

      <div className="sm:col-12 md:col-12 lg:col-5 xl:col-5  sm:px-0 md:px-0 sm:col-12 lg:pl-3">
        <div className="flex flex-column text-left">
          <Panel
            header={
              <div className="w-full flex align-items-center justify-content-between text-sm">
                <i className="pi pi-cog mr-1" style={{ fontSize: '1.3rem' }} />
                <span className="inline-block font-medium" style={{ fontSize: '1.1rem' }}>
                  {t(['project.activity'])}
                </span>
              </div>
            }
          >
            <div className="flex align-items-center my-2">
              <GitBranch size={18} className="mr-2" />
              {Object.keys(project.branches || {}).length > 1 ? (
                <span className="ml-1">
                  {Object.keys(project.branches).length} {t(['git.branches', 'branches'])}
                </span>
              ) : (
                <span className="ml-1">
                  {Object.keys(project.branches).length} {t(['git.branch', 'branch'])}
                </span>
              )}
            </div>

            <div className="flex align-items-center my-2">
              <i className="pi pi-tags mr-2" style={{ fontSize: '1.2rem' }} />
              {Object.keys(project.tags || {}).length > 1 ? (
                <span className="ml-1">
                  {Object.keys(project.tags).length} {t(['git.tags', 'tags'])}
                </span>
              ) : (
                <span className="ml-1">
                  {Object.keys(project.tags).length} {t(['git.tag', 'tag'])}
                </span>
              )}
            </div>

            <div className="block my-2">
              <span className="mb-1 inline-block font-medium">{t(['date.created_at'])}</span>
              <div className="flex align-items-center">
                <Calendar size={18} className="mr-2" />
                <span>{DateTime.fromISO(project.created_at).toLocaleString(DateTime.DATE_MED)}</span>
              </div>
            </div>
            <div className="block my-2">
              <span className="mb-1 inline-block font-medium">{t(['date.updated_at'])}</span>
              <div className="flex align-items-center">
                <CalendarSync size={18} className="mr-2" />
                <span>{DateTime.fromISO(project.updated_at).toLocaleString(DateTime.DATE_MED)}</span>
              </div>
            </div>
          </Panel>

          <Panel
            className="mt-2"
            header={
              <div className="w-full flex align-items-center justify-content-between text-sm">
                <i className="pi pi-users  mr-1" style={{ fontSize: '1.3rem' }} />
                <span className="inline-block font-medium" style={{ fontSize: '1.1rem' }}>
                  {t(['project.contributors'])}
                </span>
              </div>
            }
          >
            <GroupAvatar className="mt-1" contributors={project.contributors} />
          </Panel>

          <Panel
            className="mt-2"
            header={
              <div className="w-full flex align-items-center justify-content-between text-sm">
                <i className="pi pi-link  mr-1" style={{ fontSize: '1.3rem' }} />
                <span className="inline-block font-medium" style={{ fontSize: '1.1rem' }}>
                  {t(['project.links.title'])}
                </span>
              </div>
            }
          >
            <div className="links">
              <ul className="pl-4">
                <li className="w-full">
                  <Link href={links.provider_link} title={t(['project.links.provider'])} />
                </li>
                <li>
                  <Link href={links.api} title={'OGC API Swagger'} />
                </li>
                <li>
                  <Link
                    href={links.processes}
                    title={'OGC API Processes'}
                    formatter={processesEndpoint.hrefFormatter}
                  />
                </li>
                <li>
                  <Link
                    href={links.conformance}
                    title={'OGC API Conformance'}
                    formatter={processesEndpoint.hrefFormatter}
                  />
                </li>
              </ul>
            </div>
          </Panel>
        </div>
      </div>
    </Div>
  );
};
